import { Badge } from '@/components/ui/badge';

import {
  channelLabel,
  type HwcgActivationChannelTally,
  type HwcgActivationSummary as HwcgActivationSummaryData,
} from './hwcgActivationLogModel';

type Counts = Pick<
  HwcgActivationSummaryData,
  | 'members'
  | 'activated'
  | 'declined'
  | 'pending'
  | 'noticesSent'
  | 'usersNoticed'
  | 'step1'
  | 'step2'
> &
  Partial<Pick<HwcgActivationSummaryData, 'channels' | 'medianMinutesToRespond'>>;

function Stat({
  label,
  value,
  tone,
}: {
  label: string;
  value: string | number;
  tone?: 'success' | 'destructive' | 'warning' | 'muted';
}) {
  const toneClass =
    tone === 'success'
      ? 'tw:text-success'
      : tone === 'destructive'
        ? 'tw:text-destructive'
        : tone === 'warning'
          ? 'tw:text-warning'
          : 'tw:text-foreground';
  return (
    <div className="tw:rounded-md tw:border tw:border-border tw:bg-card tw:px-3 tw:py-2">
      <p className="tw:m-0 tw:text-[11px] tw:uppercase tw:tracking-wide tw:text-muted-foreground">
        {label}
      </p>
      <p className={`tw:m-0 tw:text-lg tw:font-semibold tw:tabular-nums ${toneClass}`}>
        {value}
      </p>
    </div>
  );
}

function TallyChips({
  title,
  tallies,
}: {
  title: string;
  tallies: Record<string, number>;
}) {
  const entries = Object.entries(tallies).sort(([a], [b]) =>
    a.localeCompare(b)
  );
  return (
    <div className="tw:space-y-1">
      <p className="tw:m-0 tw:text-[11px] tw:text-muted-foreground">{title}</p>
      <div className="tw:flex tw:flex-wrap tw:gap-1.5">
        {entries.length === 0 ? (
          <span className="tw:text-xs tw:text-muted-foreground">
            No notices yet
          </span>
        ) : (
          entries.map(([label, count]) => (
            <Badge
              key={label}
              variant={
                label === 'Activated' || label === 'Agreed'
                  ? 'green'
                  : label === 'Declined'
                    ? 'destructive'
                    : label === 'Awaiting'
                      ? 'amber'
                      : 'secondary'
              }
              className="tw:font-normal"
            >
              {label}: {count}
            </Badge>
          ))
        )}
      </div>
    </div>
  );
}

function ChannelRow({
  channel,
  tally,
}: {
  channel: string;
  tally: HwcgActivationChannelTally;
}) {
  return (
    <div className="tw:flex tw:items-center tw:justify-between tw:gap-3 tw:text-xs">
      <span className="tw:font-medium">{channelLabel(channel)}</span>
      <span className="tw:tabular-nums tw:text-muted-foreground">
        {tally.sent} sent · {tally.delivered} delivered · {tally.responded}{' '}
        replied
        {tally.failed > 0 ? (
          <span className="tw:text-destructive"> · {tally.failed} failed</span>
        ) : null}
      </span>
    </div>
  );
}

/**
 * Aggregate view of an activation push: roster status counts, how many
 * notices went out, how each step was answered, and per-channel reach.
 * Accepts the full summary or a subset recomputed for a few users.
 */
export function HwcgActivationSummary({
  summary,
  title = 'Activation summary',
  compact = false,
  oneNoticeResponses,
}: {
  summary: Counts;
  title?: string;
  compact?: boolean;
  /** When set, the section shows one response tally instead of the two step chips. */
  oneNoticeResponses?: Record<string, number>;
}) {
  const channels = Object.entries(summary.channels ?? {}).sort(([a], [b]) =>
    a.localeCompare(b)
  );
  return (
    <section
      className="tw:space-y-3"
      aria-label={title}
      data-tour="hwcg-activation-summary"
    >
      {title ? (
        <h3 className="tw:m-0 tw:text-sm tw:font-semibold">{title}</h3>
      ) : null}
      <div
        className={`tw:grid tw:gap-2 ${compact ? 'tw:grid-cols-2 tw:sm:grid-cols-4' : 'tw:grid-cols-2 tw:sm:grid-cols-3 tw:lg:grid-cols-6'}`}
      >
        <Stat label="Roster members" value={summary.members} />
        <Stat label="Activated" value={summary.activated} tone="success" />
        <Stat label="Declined" value={summary.declined} tone="destructive" />
        <Stat label="Pending" value={summary.pending} tone="warning" />
        {!compact ? (
          <>
            <Stat
              label="Notices sent"
              value={`${summary.noticesSent} to ${summary.usersNoticed}`}
            />
            <Stat
              label="Median reply"
              value={
                summary.medianMinutesToRespond === null ||
                summary.medianMinutesToRespond === undefined
                  ? '—'
                  : `${summary.medianMinutesToRespond} min`
              }
            />
          </>
        ) : null}
      </div>
      {oneNoticeResponses ? (
        <TallyChips title="Response" tallies={oneNoticeResponses} />
      ) : (
        <div className="tw:grid tw:gap-3 tw:sm:grid-cols-2">
          <TallyChips title="Step 1 · Confidentiality" tallies={summary.step1} />
          <TallyChips
            title="Step 2 · Activation notice"
            tallies={summary.step2}
          />
        </div>
      )}
      {channels.length > 0 ? (
        <div className="tw:space-y-1 tw:rounded-md tw:border tw:border-border tw:p-2">
          <p className="tw:m-0 tw:text-[11px] tw:text-muted-foreground">
            By channel
          </p>
          {channels.map(([channel, tally]) => (
            <ChannelRow key={channel} channel={channel} tally={tally} />
          ))}
        </div>
      ) : null}
    </section>
  );
}

export default HwcgActivationSummary;
